// Depth-aware water.
//
// The two things that make water read as water — shallows lightening near the
// shore, and a foam band where land meets it — both need to know how much water
// sits between the surface and whatever is underneath. That distance isn't in
// the geometry (the surface is a flat plane), so it's recovered from the scene's
// depth buffer: render the scene once without the water, and the difference
// between that depth and the water fragment's own depth is the water depth.
//
// Everything else follows from that one number: alpha, tint, and foam.
//
// The depth pass is a second scene render, which is too expensive for Quest and
// phones — those fall back to flat alpha blending. The GPU's own texture limit
// is the gate rather than a user-agent sniff, because Quest Browser reports
// itself as desktop Linux.

import React, { useMemo, useRef, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

// Shared by every water surface — the depth of the scene is the same for all of
// them, so it is rendered once per frame no matter how many pools exist.
const depthTarget = new THREE.WebGLRenderTarget(1, 1);
depthTarget.depthTexture = new THREE.DepthTexture(1, 1);
depthTarget.depthTexture.format = THREE.DepthFormat;
// UnsignedShort quantises visibly over a large ground plane — the foam band
// stair-steps. Int precision costs nothing here.
depthTarget.depthTexture.type = THREE.UnsignedIntType;

const depthState = { surfaces: 0, valid: false };

/** Cheap stand-in during the depth pass: we only want the z-buffer, not colour. */
const depthOverride = new THREE.MeshBasicMaterial();

/**
 * Renders scene depth once per frame while any water is present. Mount once,
 * high in the scene tree; it draws nothing itself.
 */
export const WaterDepthPass: React.FC = () => {
  const { gl, scene, camera, size, viewport } = useThree();

  useEffect(() => {
    const w = Math.max(1, Math.min(1024, Math.round(size.width * viewport.dpr)));
    const h = Math.max(1, Math.min(1024, Math.round(size.height * viewport.dpr)));
    depthTarget.setSize(w, h);
  }, [size.width, size.height, viewport.dpr]);

  // Priority MUST stay 0. A useFrame with priority > 0 makes react-three-fiber
  // hand the render loop over to the callback and stop drawing the scene
  // itself — the viewport goes black everywhere, not just on water. Priority 0
  // callbacks run before the frame is drawn, which is the ordering we need.
  useFrame(() => {
    if (depthState.surfaces <= 0) {
      depthState.valid = false;
      return;
    }
    // Only opaque geometry counts as the bottom of the water. Transparent
    // objects are skipped for a concrete reason: the editor grid is a
    // full-screen transparent plane sitting at exactly y=0, and the opaque
    // override below would turn it into a solid floor coincident with the water
    // surface — every fragment would read zero depth and the whole surface would
    // foam over. Water itself is excluded for the obvious reason.
    const hidden: THREE.Object3D[] = [];
    scene.traverse((o) => {
      const mat = (o as THREE.Mesh).material as THREE.Material | undefined;
      if (!o.visible || !mat || Array.isArray(mat)) return;
      if (mat.userData?.__gxWater || mat.transparent) {
        o.visible = false;
        hidden.push(o);
      }
    });

    const prevTarget = gl.getRenderTarget();
    const prevOverride = scene.overrideMaterial;
    scene.overrideMaterial = depthOverride;
    gl.setRenderTarget(depthTarget);
    gl.clear();
    gl.render(scene, camera);
    gl.setRenderTarget(prevTarget);
    scene.overrideMaterial = prevOverride;

    hidden.forEach((o) => { o.visible = true; });
    depthState.valid = true;
  }, 0);

  return null;
};

// Per-preset palettes. The depth tint is what gives each variant its
// character, so it travels with the preset rather than living on the texture.
// `detail` is (bias, gain) on the colour map's luminance, which re-applies the
// ripple contrast the depth tint would otherwise flatten. It has to travel with
// the palette: a fixed gain tuned on the dark ocean texture drives the pale one
// straight past white, because the same multiplier is applied to a far brighter
// starting luminance.
export const WATER_PALETTES: Record<string, {
  shallow: string; deep: string; foam: string; depthFade: number; detail: [number, number];
}> = {
  // Deep ocean: strong contrast between shallows and depth.
  water: { shallow: '#2fa6ad', deep: '#06323f', foam: '#eaf7f7', depthFade: 3.0, detail: [0.70, 1.6] },
  // Ethereal: pale and luminous. The deep end stays light — the whole point is
  // that it never goes murky — so the tint range is narrow and the depth fades
  // over a longer distance, which keeps the falloff gradual instead of banding
  // across two near-identical colours.
  water2: { shallow: '#bfeef7', deep: '#4a9fc4', foam: '#ffffff', depthFade: 6.0, detail: [0.42, 0.62] },
};

export interface WaterSurfaceProps {
  texMap: Record<string, any>;
  /** Preset id — selects the depth palette. */
  preset?: string;
  /** Ground transparency multiplier, 0..1. */
  opacity?: number;
  /** How far down the water reaches full deep colour, in metres. */
  depthFade?: number;
  /** Width of the foam band at the shoreline, in metres. */
  foamWidth?: number;
  color?: string;
}

/**
 * A `<meshStandardMaterial>` that tints, fades and foams by water depth.
 * Falls back to plain translucency where the depth pass is too expensive.
 */
export const WaterSurface: React.FC<WaterSurfaceProps> = ({
  texMap,
  preset = 'water',
  opacity = 1,
  depthFade,
  foamWidth = 0.35,
  color = '#ffffff',
}) => {
  const palette = WATER_PALETTES[preset] ?? WATER_PALETTES.water;
  const fade = depthFade ?? palette.depthFade;
  const { gl } = useThree();
  const rich = (gl?.capabilities?.maxTextureSize ?? 8192) > 4096;
  const matRef = useRef<THREE.MeshStandardMaterial>(null);

  // Anisotropic filtering matters more here than on any other surface: the
  // ground is viewed almost edge-on for most of the screen, and without it the
  // tiled ripples alias into crawling blotches towards the horizon.
  useEffect(() => {
    const max = gl?.capabilities?.getMaxAnisotropy?.() ?? 1;
    Object.values(texMap).forEach((tex: any) => {
      if (tex instanceof THREE.Texture) {
        tex.anisotropy = Math.min(8, max);
        tex.needsUpdate = true;
      }
    });
  }, [texMap, gl]);

  const uniforms = useMemo(
    () => ({
      uSceneDepth: { value: depthTarget.depthTexture },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uNear: { value: 0.1 },
      uFar: { value: 1000 },
      uShallow: { value: new THREE.Color(palette.shallow) },
      uDeep: { value: new THREE.Color(palette.deep) },
      uFoam: { value: new THREE.Color(palette.foam) },
      uDepthFade: { value: fade },
      uFoamWidth: { value: foamWidth },
      uHasDepth: { value: 0 },
      uAlpha: { value: 1 },
      uDetail: { value: new THREE.Vector2(palette.detail[0], palette.detail[1]) },
    }),
    [palette, fade, foamWidth],
  );

  // Ref-count so the depth pass idles when no water is in the world.
  useEffect(() => {
    if (!rich) return;
    depthState.surfaces += 1;
    return () => { depthState.surfaces -= 1; };
  }, [rich]);

  useFrame(({ camera, size, viewport }) => {
    uniforms.uResolution.value.set(
      Math.max(1, Math.min(1024, Math.round(size.width * viewport.dpr))),
      Math.max(1, Math.min(1024, Math.round(size.height * viewport.dpr))),
    );
    const cam = camera as THREE.PerspectiveCamera;
    uniforms.uNear.value = cam.near;
    uniforms.uFar.value = cam.far;
    uniforms.uHasDepth.value = depthState.valid ? 1 : 0;
    uniforms.uAlpha.value = opacity;
  });

  if (!rich) {
    return (
      <meshStandardMaterial
        color={color}
        map={texMap.map}
        normalMap={texMap.normalMap}
        roughnessMap={texMap.roughnessMap}
        roughness={1}
        metalness={0.15}
        transparent
        opacity={0.88 * opacity}
      />
    );
  }

  return (
    <meshStandardMaterial
      ref={matRef}
      color={color}
      map={texMap.map}
      normalMap={texMap.normalMap}
      roughnessMap={texMap.roughnessMap}
      roughness={1}
      metalness={0}
      transparent
      // Water must not occlude what is under it in the transparent pass.
      depthWrite={false}
      // Water is already alpha-blended per fragment, so the slider is folded
      // into the shader (uAlpha) rather than set here — material.opacity would
      // be overwritten by the depth-driven alpha the shader computes.
      visible={opacity > 0}
      userData={{ __gxWater: true }}
      onUpdate={(self: THREE.MeshStandardMaterial) => {
        if (self.userData.__gxWired) return;
        self.userData.__gxWired = true;
        self.userData.__gxWater = true;
        self.onBeforeCompile = (shader) => {
          Object.assign(shader.uniforms, uniforms);
          shader.fragmentShader = shader.fragmentShader
            .replace(
              '#include <common>',
              `#include <common>
              uniform sampler2D uSceneDepth;
              uniform vec2 uResolution;
              uniform float uNear;
              uniform float uFar;
              uniform vec3 uShallow;
              uniform vec3 uDeep;
              uniform vec3 uFoam;
              uniform float uDepthFade;
              uniform float uFoamWidth;
              uniform float uHasDepth;
              uniform vec2 uDetail;
              uniform float uAlpha;`,
            )
            // After normal mapping, so the ripples are available to wobble the
            // shoreline — a foam band traced from flat depth alone looks like a
            // contour line drawn around each object.
            .replace(
              '#include <normal_fragment_maps>',
              `#include <normal_fragment_maps>
              {
                float gxDepth = uDepthFade;
                if (uHasDepth > 0.5) {
                  vec2 gxUv = gl_FragCoord.xy / uResolution;
                  float gxNdc = texture2D(uSceneDepth, gxUv).x * 2.0 - 1.0;
                  float gxScene = (2.0 * uNear * uFar)
                                / (uFar + uNear - gxNdc * (uFar - uNear));
                  gxDepth = max(gxScene - vViewPosition.z, 0.0);
                }
                gxDepth = max(gxDepth + (normal.x + normal.z) * 0.10, 0.0);

                float gxDeepT = clamp(gxDepth / max(uDepthFade, 0.001), 0.0, 1.0);
                float gxFoam = 1.0 - smoothstep(0.0, max(uFoamWidth, 0.001), gxDepth);

                // Keep the ripple detail from the colour map as luminance only,
                // so the depth tint decides the hue.
                float gxDetail = dot(diffuseColor.rgb, vec3(0.3333));
                diffuseColor.rgb = mix(uShallow, uDeep, gxDeepT)
                                 * (uDetail.x + gxDetail * uDetail.y);
                diffuseColor.rgb = mix(diffuseColor.rgb, uFoam, gxFoam);
                diffuseColor.a = max(mix(0.32, 0.95, gxDeepT), gxFoam) * uAlpha;
              }`,
            );
        };
        self.needsUpdate = true;
      }}
    />
  );
};

export default WaterSurface;
